import type { ReduxSagaModel } from './types';
import { history, resetAllModelStatesOnLogout } from 'umi';
import { Auth } from '@/aws-config';
import {
  AmplifyLogin,
  AmplifyLogout,
  AmplifyForgotPassword,
  AmplifyNewPassword,
  CognitoUseAuthCode,
} from '@/services/login';
import { setAuthority } from '@/utils/authority';
import { LOGIN_REDIRECT } from '@/utils/constants';
import User from '@/services/user';
import { sgcall } from '@/utils/reduxSaga';
import { getAuth0LogoutURL, getLogoutURL } from '@/utils/awsCognito';
import { handleSystemError } from '@/utils/handleError';
import { logger } from '@/utils/logger';
import { assertIsDefined } from '@/utils/typeChecks';
import mixpanel from 'mixpanel-browser';

const ns = 'app.models.login';

export type State = {
  currentAuthority: string;
  status: string | undefined;
  user: unknown | undefined;
  error: unknown | undefined;
  email: string | undefined;
};

const initialState: State = {
  currentAuthority: 'guest',
  status: undefined,
  user: undefined,
  error: undefined,
  email: undefined,
};

type ChangeLoginStatusAction = {
  type: 'changeLoginStatus';
  payload: Partial<State>;
};

export type LogoutEffect = {
  type: 'login/logout';
  payload: { redirect?: string };
};

const handleResponse = (status: string): void => {
  if (status === 'ok') {
    const redirect = sessionStorage.getItem(LOGIN_REDIRECT);
    if (redirect) {
      sessionStorage.removeItem(LOGIN_REDIRECT);
      const currentURL = new URL(window.location.href);
      const redirectURL = new URL(redirect, currentURL.origin);
      if (redirectURL.origin === currentURL.origin) {
        history.push(redirectURL.pathname + redirectURL.search + redirectURL.hash);
        return;
      }
    }
    history.push('/login-success');
  } else if (status === 'new_password_required') {
    history.push('/account/new-password');
  } else if (status === 'error') {
    return;
  } else {
    throw new Error('BEEM241001155707');
  }
};

const LoginModel: ReduxSagaModel<
  State,
  {
    changeLoginStatus: ChangeLoginStatusAction;
  }
> = {
  namespace: 'login',
  state: initialState,
  effects: {
    *login({ payload }, { put }) {
      try {
        logger.debug(payload, { label: 'app.models.login.login.payload' });
        const response = payload.code
          ? yield* sgcall(() => CognitoUseAuthCode(payload.code))
          : yield* sgcall(() => AmplifyLogin(payload));
        logger.debug({ label: `${ns}.login.response`, message: response });

        yield put<ChangeLoginStatusAction>({ type: 'changeLoginStatus', payload: response });
        const isMixpanelLoaded = (mixpanel as unknown as { __loaded?: boolean }).__loaded;
        if (isMixpanelLoaded) {
          mixpanel.track('Sign In', {
            user_id: payload.email,
            login_method: payload.code ? 'oauth' : 'email',
            success: response.status === 'ok' || response.status === 'new_password_required',
          });
        }
        handleResponse(response.status);

        if (payload?.isFirstLogin === '1') {
          const { firstName, lastName } = payload;
          const rs = yield* sgcall(User.getCurrentAuthenticatedUser);
          assertIsDefined(rs, 'BEEM230905050300');
          yield* sgcall(() => User.updateUserDetails({ id: rs.id, firstName, lastName }));
          window.location.reload();
        }
      } catch (e) {
        handleSystemError(e, 'BEEM230817092158');
      }
    },

    *newPassword({ payload }, { put }) {
      logger.debug(payload, { label: 'app.models.login.newPassword.payload' });
      const response = yield* sgcall(() => AmplifyNewPassword(payload));
      logger.debug({ label: `${ns}.newPassword.response`, message: response });

      yield put<ChangeLoginStatusAction>({ type: 'changeLoginStatus', payload: response });

      handleResponse(response.status);
    },

    *forgotPassword({ payload }, { put }) {
      const { username, newPassword, firstName, lastName } = payload;

      const currentUser = yield* sgcall(User.getCurrentAuthenticatedUser);
      if (currentUser) {
        const logoutResponse = yield* sgcall(() => AmplifyLogout());
        logger.debug({ label: `${ns}.forgotPassword.logoutResponse`, message: logoutResponse });
      }

      logger.debug(payload, { label: 'app.models.login.forgotPassword.payload' });
      const response = yield* sgcall(() => AmplifyForgotPassword(payload));
      logger.debug({ label: `${ns}.forgotPassword.response`, message: response });

      yield put<ChangeLoginStatusAction>({ type: 'changeLoginStatus', payload: response });

      const isChangeSuccess = response.status === 'change_success';

      if (payload?.isFirstLogin === '1' && isChangeSuccess) {
        yield put({
          type: 'login',
          payload: {
            email: username,
            password: newPassword,
            firstName,
            lastName,
            isFirstLogin: payload.isFirstLogin,
          },
        });
        return;
      }

      if (isChangeSuccess) {
        window.location.href = '/';
      }
    },

    *resetPasswordForAnotherUser({ payload }) {
      yield* sgcall(() => AmplifyForgotPassword(payload));
    },

    *logout({ payload = {} }: { payload: LogoutEffect['payload'] }, { put }) {
      const { redirect } = payload;
      if (redirect) sessionStorage.setItem(LOGIN_REDIRECT, redirect);

      if (!getAuth0LogoutURL()) {
        // this is to support the dev and pr envs that are not using auth0 yet
        const response = yield* sgcall(() => AmplifyLogout());
        logger.debug({ label: `${ns}.logout.response`, message: response });
        yield put<ChangeLoginStatusAction>({ type: 'changeLoginStatus', payload: response });
        yield* resetAllModelStatesOnLogout(put);
        history.push('/login');
        return;
      }

      const isLoggedIn = yield* sgcall(async () => {
        const user = await Auth.currentUserInfo();
        logger.debug({ label: `${ns}.logout.user`, message: user === null ? 'null' : user });
        return !!user;
      });

      if (isLoggedIn) {
        const response = yield* sgcall(() => AmplifyLogout());
        logger.debug({ label: `${ns}.logout.response`, message: response });
        window.location.assign(getLogoutURL(!!redirect));
      } else {
        // this happens when the user is not logged in, and he goes directly to the app
        // so the SecurityLayout invokes kickoutUser -> login/logout effect
        window.location.assign(getAuth0LogoutURL());
      }
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    changeLoginStatus(state, { payload }) {
      // this currentAuthority is only 'guest' or 'VIEWER' to indicate the user is logged in
      // the actual authority is set in the `organization/use` effect
      setAuthority(payload.currentAuthority);
      return {
        ...state,
        status: payload.status,
        user: payload.user,
        error: payload.error,
        email: payload.email,
      };
    },
  },
};

export default LoginModel;
